import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionEnteralDietViewFragmentAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { EnteralDietResponse, EnteralDietTypes } from './enteral-diet-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { AdministrationType, DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.NUTRITION_ENTERAL_DIET_VIEW_FRAGMENT })
class EnteralDietViewController extends BaseController {

  @Inject(NutritionEnteralDietViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FORM_ADMINISTRATION_INFORMATION)
  formAdministrationInformationAPI!: FormAPI

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_TOTAL_VOLUME)
  fieldTotalVolumeAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_BAG_VOLUME_ML)
  fieldBagVolumeAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_DURATION_H_MIN)
  fieldDurationAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_STAGE_INFUSION_DURATION)
  fieldStageInfusionDurationAPI!: FieldAPI;

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_INFUSION_RATE)
  fieldInfusionRateAPI!: FieldAPI

  @Inject(NutritionEnteralDietViewFragmentAliases.FIELD_INFUSION_RATE_UNIT_OF_MEASUREMENT)
  fieldInfusionRateUnitOfMeasurementAPI!: FieldAPI

  @OnMount(NutritionEnteralDietViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionId, patientId } = this.historyService.getCurrentRouteParams();
    this.fetchEnteralDietDetails(String(nutritionId), String(patientId));
  }

  @OnMount(NutritionEnteralDietViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const enteralDietView = this.context.get('enteralDietView') as EnteralDietResponse;
    if (enteralDietView) {
      const {
        administrationRoute,
        dose,
        material,
        measurementUnit
      } = enteralDietView;

      this.formMainInformationsAPI.setValue({
        administrationRoute,
        dose,
        material,
        measurementUnit
      });
    }
  }

  @OnMount(NutritionEnteralDietViewFragmentAliases.FORM_ADMINISTRATION_INFORMATION)
  handleMountFormAdministrationInformation(): void {
    const enteralDietView = this.context.get('enteralDietView') as EnteralDietResponse
    if (enteralDietView) {
      const { interval, enteralDiet, applicationTime, dosageType, infusionSpeed, totalVolume, infusionBomb, orientation, enteralDietType } = enteralDietView;
      this.formAdministrationInformationAPI.setValue({
        applicationTime,
        dosageType,
        enteralDiet,
        infusionBomb,
        infusionSpeed,
        interval,
        orientation,
        totalVolume,
      });
      this.showFieldsByEnteralDietType(enteralDietType, enteralDietView)
      this.fieldInfusionRateAPI.setVisible(!!infusionSpeed)
      this.fieldInfusionRateUnitOfMeasurementAPI.setVisible(!!infusionSpeed)
    }
  }

  @OnMount(NutritionEnteralDietViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const enteralDietView = this.context.get('enteralDietView') as EnteralDietResponse;

    if (enteralDietView) {
      const { duration, hours, administration, administrationType, durationType } = enteralDietView;
      const startDate = moment(String(enteralDietView.startDate)).utc().format(ISO_DATE_INPUT);
      const endDate = moment(String(enteralDietView.endDate)).utc().format(ISO_DATE_INPUT);

      this.formPeriodValidityAPI.setValue({
        administration,
        duration,
        endDate,
        hours,
        startDate
      });

      this.inputStartDateAPI.setValue(startDate);
      this.inputEndDateAPI.setValue(endDate);
      this.showFieldsByDurationType(durationType);
      this.showFieldsByAdministrationType(administrationType);
    }
  }

  @OnMount(NutritionEnteralDietViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const enteralDietView = this.context.get('enteralDietView') as EnteralDietResponse;
    if (enteralDietView) {
      const { prescriberDt, prescriberName, observation, justification } = enteralDietView;

      this.formAddicionalInformationsAPI.setValue({
        justification,
        observation,
        prescriberDt,
        prescriberName
      });

      this.fieldObservationAPI.setVisible(!!observation);
      this.fieldJustificationAPI.setVisible(!!justification);
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName);
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt);
    }
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED);
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME);
  }

  private showFieldsByEnteralDietType(enteralDietType: string, enteralDietView: EnteralDietResponse): void {
    if (enteralDietType === EnteralDietTypes.CONTINUOUS) {
      this.fieldBagVolumeAPI.setVisible(false)
      this.fieldStageInfusionDurationAPI.setVisible(false)
      this.fieldDurationAPI.setVisible(!!enteralDietView.applicationTime)
      this.fieldTotalVolumeAPI.setVisible(true)
    }

    if (enteralDietType === EnteralDietTypes.INTERMITTENT) {
      this.fieldTotalVolumeAPI.setVisible(false)
      this.fieldDurationAPI.setVisible(false)
      this.fieldBagVolumeAPI.setVisible(true)
      this.fieldStageInfusionDurationAPI.setVisible(!!enteralDietView.applicationTime)
    }
  }

  private fetchEnteralDietDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true);
    this.panelContentAPI.setVisible(false);
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_ENTERAL_DIET_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const enteralDietView = response.body as EnteralDietResponse;
        this.context.set({ enteralDietView });
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false);
        this.panelContentAPI.setVisible(true);
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-enteral-diet',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }
}

export default EnteralDietViewController;